import type { HierarchyTrailItem } from '@vizit/access';
import { EntityBadge, EntityBadgeSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { ChevronRightIcon } from '@vizit/icons';
import { colors, cva, cx } from '@vizit/theme';

const styles = {
  bar: cva({ base: 'w-full flex flex-col items-start bg-brand-900 px-6 py-2' }),
  list: cva({ base: 'flex items-center gap-2' }),
  item: cva({ base: 'flex items-center gap-2' }),
  level: cva({
    base: 'flex items-center justify-center gap-2 max-w-[280px] px-4 py-2 outline-none',
    variants: {
      current: { true: 'bg-brand-800 text-brand-200', false: 'text-brand-300' },
      interactive: {
        true: [
          'group cursor-pointer transition-colors',
          'hover:bg-brand-800 hover:text-brand-200',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400',
        ],
        false: '',
      },
    },
  }),
  label: cva({ base: 'min-w-0 group-hover:underline' }),
};

export interface HierarchyBreadcrumbsProps {
  items: HierarchyTrailItem[];
  onSelect: (item: HierarchyTrailItem) => void;
  className?: string;
  'aria-label'?: string;
}

function HierarchyBreadcrumbs(props: HierarchyBreadcrumbsProps) {
  const { items, onSelect, className, 'aria-label': ariaLabel = 'Organization hierarchy' } = props;

  if (items.length === 0) {
    return null;
  }

  return (
    <nav aria-label={ariaLabel} className={cx(styles.bar(), className)} data-testid="hierarchy-breadcrumbs">
      <ol className={styles.list()}>
        {items.map((item, index) => {
          const ariaCurrent = item.isCurrent ? ('page' as const) : undefined;
          const content = (
            <>
              <EntityBadge id={item.id} name={item.name} size={EntityBadgeSize.Sm} />
              <Text
                size={TextSize.Xs}
                weight={TextWeight.Semibold}
                color={TextColor.Inherit}
                showEllipsis
                className={styles.label()}
                title={item.name}
              >
                {item.name}
              </Text>
            </>
          );

          return (
            <li key={item.id} className={styles.item()}>
              {index > 0 ? <ChevronRightIcon size={16} color={colors.brand['400']} aria-hidden="true" /> : null}

              {item.isInteractive ? (
                <button
                  type="button"
                  aria-current={ariaCurrent}
                  className={styles.level({ current: item.isCurrent, interactive: true })}
                  onClick={() => onSelect(item)}
                  data-testid={`hierarchy-breadcrumb-${item.kind}`}
                >
                  {content}
                </button>
              ) : (
                <span
                  aria-current={ariaCurrent}
                  className={styles.level({ current: item.isCurrent, interactive: false })}
                  data-testid={`hierarchy-breadcrumb-${item.kind}`}
                >
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export { HierarchyBreadcrumbs };
